import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { User } from "../types";
import { tokenStorage, userStorage, ApiError } from "../api/client";
import { auth, users as usersApi, SignupRequest, LoginRequest } from "../api/endpoints";

/**
 * AuthContext exposes the logged-in user, the JWT token, and methods for the
 * login / signup / logout flow.  The token is persisted to localStorage under
 * `sajha_token` and replayed into every API request via `apiClient`.
 */
interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /** True while we're booting and validating an existing token. */
  loading: boolean;
  login: (body: LoginRequest) => Promise<User>;
  signup: (body: SignupRequest) => Promise<{ user: User; devCode?: string }>;
  logout: () => void;
  /** Refresh the cached user from /me (e.g., after profile edits). */
  refresh: () => Promise<User | null>;
  /** Directly update the cached user (e.g., after a PATCH /me). */
  setUser: (u: User | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => tokenStorage.get());
  const [user, setUserState] = useState<User | null>(() => userStorage.get<User>());
  const [loading, setLoading] = useState<boolean>(!!tokenStorage.get());

  /* On boot: if we have a cached token, verify it by fetching /me. */
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const me = await usersApi.me();
        if (cancelled) return;
        setUserState(me);
        userStorage.set(me);
      } catch (err) {
        if (cancelled) return;
        // Token rejected (or backend down) — clear so we don't loop.
        if (err instanceof ApiError && err.status === 401) {
          tokenStorage.clear();
          setTokenState(null);
          setUserState(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // We only want this to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUser = useCallback((u: User | null) => {
    setUserState(u);
    if (u) userStorage.set(u);
  }, []);

  const login = useCallback(async (body: LoginRequest) => {
    const res = await auth.login(body);
    tokenStorage.set(res.token);
    userStorage.set(res.user);
    setTokenState(res.token);
    setUserState(res.user);
    return res.user;
  }, []);

  const signup = useCallback(async (body: SignupRequest) => {
    const res = await auth.signup(body);
    // Do NOT set a token here — the user still has to verify their email.
    return { user: res.user, devCode: res.dev_code };
  }, []);

  const logout = useCallback(() => {
    tokenStorage.clear();
    setTokenState(null);
    setUserState(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!tokenStorage.get()) return null;
    try {
      const me = await usersApi.me();
      setUserState(me);
      userStorage.set(me);
      return me;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: !!token && !!user,
      loading,
      login,
      signup,
      logout,
      refresh,
      setUser,
    }),
    [user, token, loading, login, signup, logout, refresh, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
